// Built-in salt water chlorinators. Owner, 2026-10-27 (photo of the Dunlop Park supplier folder, AIS Water, the file
// SRC-200-100F-AutoChlor-Technical-Datasheet.pdf selected): "get the technical drawings and put them into the TraceCAD
// 2D ... make it into DXF components and put it in there".
//
// SOURCE. AIS Water, AutoChlor SRC-200-100F technical datasheet (2025), page 2: the top view, the end view, two
// isometric views and the plan view of the service clearance. The views in the PDF are pictures, not lines, so each
// view here is TRACED: the picture scale is fixed by the dimensions printed on it, and each line is measured there.
//
// WHAT IS EXACT AND WHAT IS TRACED.
//   Printed on the datasheet, drawn at that number: depth 510, height 1070; inlet 733 and outlet 1003 from the cabinet
//   end (733 + 270, then 105 to the far end, so 1108 wide: the size table rounds it to 1110, the picture measures 1108); inlet and outlet 100 below the top; drain 163 above the floor;
//   door swing R496; service clearance 500 at the cabinet end, 500 at the back, 1000 at the far end, 500 past the open
//   door. Pipes: inlet and outlet 2" (DN50) PVC schedule 80, acid drain 3/4" (DN20).
//   Traced from the pictures (about ±5 mm): the cabinet 490 wide, 484 deep; the two cells 225 across, 122 from the
//   front, centres 278 and 604 above the floor; the drain 113 from the back; the frame members; the valve bodies.
//   The datasheet prints "405" in a box over the "365" of the end view (the frame from the front leg). The picture
//   measures 365 and nothing here depends on that number.
// So the OUTLINE, the CONNECTION POSITIONS and the CLEARANCE are right for a layout; the inside is a picture.
//
// FRAME. Plan: 0,0 is the back corner at the cabinet end; x along the width, y towards the front (down the screen).
// End view: seen from the far end (the end with the cells), the front on the left; x from the front, y up = -height.
// Each object is ONE group of lines, circles, arcs and texts, centred on 0,0 like a saved library object, with its
// pipe ports in meta.ports (the Pipe tool starts a pipe there; see pipes/api.ts).
import type { Vec } from "../core/geometry";
import type { Entity, Style, TextEntity } from "../doc/entities";
import { defaultTextStyle } from "../doc/document";
import type { LibraryItem } from "./library";
import type { FittingPortData } from "../pipes/api";
import { CENTRE, HIDDEN, OUTLINE } from "./fittings";
import { boundsOf } from "./pumps";

export const CHLORINATOR_CATEGORY = "Chlorinators · AIS AutoChlor";

export type ChlorinatorView = "plan" | "plan-clearance" | "side";

/** 3.32.0: the block of each view in the DXF files (dxfOut.ts). The TraceCAD import knows them by these names. */
export const CHLORINATOR_DXF_BLOCK: Record<ChlorinatorView, string> = {
  plan: "AIS_SRC-200-100F_PLAN",
  "plan-clearance": "AIS_SRC-200-100F_PLAN_CLEAR",
  side: "AIS_SRC-200-100F_END"
};

/** What a placed chlorinator is (meta.equipment of its group). */
export interface EquipmentInfo {
  kind: "chlorinator";
  maker: string;
  model: string;
  view: ChlorinatorView;
  source: string;
  /** The name of each port, in the order of meta.ports. */
  portNames: string[];
}

const FINE: Style = { ...OUTLINE, strokeWidth: 1 };
const CLEAR: Style = { ...OUTLINE, strokeWidth: 1, dash: "dashed", opacity: 0.6 };

/** The SRC-200-100F, mm. Printed = on the datasheet; the rest traced (see the top of the file). */
export const SRC_200_100F = {
  W: 733 + 270 + 105, D: 510, H: 1070, // printed (the size table says 1110 wide)
  inX: 733, outX: 1003, pipeDrop: 100, drainH: 163, door: 496, // printed
  clear: { end: 500, back: 500, far: 1000, front: 500 }, // printed
  cabW: 490, cabD: 484, cabBase: 135, // traced
  cellR: 112.5, cellFront: 122, cellH: [278, 604], cellX0: 671, cellX1: 1084, // traced
  riserY: 375, drainY: 113, dnMain: 50, dnDrain: 20, odMain: 60.3, odDrain: 26.7
} as const;

class Builder {
  readonly entities: Entity[] = [];
  private n = 0;
  constructor(private readonly key: string) {}
  private id(): string {
    return `${this.key}-${++this.n}`;
  }
  line(points: Vec[], closed = false, style: Style = OUTLINE): void {
    this.entities.push({ id: this.id(), type: "polyline", layerId: "equipment", locked: false, hidden: false, points, closed, style: { ...style } });
  }
  rect(x0: number, y0: number, x1: number, y1: number, style: Style = OUTLINE): void {
    this.line([{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }], true, style);
  }
  circle(center: Vec, r: number, style: Style = OUTLINE): void {
    this.entities.push({ id: this.id(), type: "circle", layerId: "equipment", locked: false, hidden: false, center, r, style: { ...style } });
  }
  arc(center: Vec, r: number, startDeg: number, sweepDeg: number, style: Style = OUTLINE): void {
    this.entities.push({ id: this.id(), type: "arc", layerId: "equipment", locked: false, hidden: false, center, r, startDeg, sweepDeg, style: { ...style } });
  }
  /** A one line text centred on (cx, cy); `up` turns it to run up the page. */
  textAt(cx: number, cy: number, content: string, size: number, up = false): void {
    const lineH = size * 1.3; // the app draws a line 1.3 × the size high, anchored at its top
    const t: TextEntity = {
      id: this.id(), type: "text", layerId: "equipment", locked: false, hidden: false,
      pos: up ? { x: cx - lineH / 2, y: cy } : { x: cx, y: cy - lineH / 2 },
      rotationDeg: up ? -90 : 0, content,
      textStyle: { ...defaultTextStyle(), size, align: "center" }
    };
    this.entities.push(t);
  }
}

/** The plan (top view), as the datasheet shows it: the cabinet door open at 90°, with its swing. */
function plan(b: Builder): FittingPortData[] {
  const s = SRC_200_100F;
  const r = s.odMain / 2;
  // the cabinet (its back panel inside), the open door on the hinge at the front corner next to the skid, its swing
  b.rect(0, 0, s.cabW, s.cabD);
  b.line([{ x: 0, y: 39 }, { x: s.cabW, y: 39 }], false, FINE);
  b.rect(s.cabW - 20, s.cabD, s.cabW, s.cabD + s.door, FINE);
  b.arc({ x: s.cabW, y: s.cabD }, s.door, 90, 90, FINE);
  // the skid frame, and the top cell under the pipes
  b.rect(s.cabW, 0, s.W, s.D);
  b.rect(s.cellX0, s.riserY - s.cellR, s.cellX1, s.riserY + s.cellR, FINE);
  // inlet and outlet: from the connection at the back, through the valve, to the drop into the cell
  for (const x of [s.inX, s.outX]) {
    b.line([{ x: x - r, y: 0 }, { x: x - r, y: s.riserY }]);
    b.line([{ x: x + r, y: 0 }, { x: x + r, y: s.riserY }]);
    b.circle({ x, y: s.riserY }, r);
    b.rect(x - 22, 35, x + 22, 150, FINE);
    b.line([{ x, y: -25 }, { x, y: s.riserY + r + 25 }], false, CENTRE);
  }
  // the acid drain, out of the far end
  b.rect(s.W - 30, s.drainY - s.odDrain / 2, s.W, s.drainY + s.odDrain / 2, FINE);
  b.textAt(s.cabW / 2, 190, "AIS AutoChlor", 40);
  b.textAt(s.cabW / 2, 250, "SRC-200-100F", 40);
  b.textAt(s.inX - r - 22, 250, "IN DN50", 22, true);
  b.textAt(s.outX - r - 22, 250, "OUT DN50", 22, true);
  b.textAt(s.W + 60, s.drainY - 30, "DRAIN DN20", 16); // outside the far end: clear of the outlet pipe
  return [
    { p: { x: s.inX, y: 0 }, dir: { x: 0, y: -1 }, size: s.dnMain },
    { p: { x: s.outX, y: 0 }, dir: { x: 0, y: -1 }, size: s.dnMain },
    { p: { x: s.W, y: s.drainY }, dir: { x: 1, y: 0 }, size: s.dnDrain }
  ];
}

/** The service clearance of the datasheet plan view, round the plan: 500, 500, 1000, and 500 past the open door. */
function clearance(b: Builder): void {
  const s = SRC_200_100F;
  const x0 = -s.clear.end, x1 = s.W + s.clear.far;
  const y0 = -s.clear.back, y1 = s.cabD + s.door + s.clear.front;
  b.rect(x0, y0, x1, y1, CLEAR);
  b.textAt((x0 + x1) / 2, y0 + 60, "SERVICE CLEARANCE (AIS DATASHEET): KEEP CLEAR", 36);
}

/** The end view from the far end (the cells end on); the cabinet behind it is hidden. */
function side(b: Builder): FittingPortData[] {
  const s = SRC_200_100F;
  const r = s.odMain / 2;
  const back = s.D;
  // the frame: front and back posts, top and bottom rails, the post at the pipes
  b.rect(0, -s.H, 25, 0);
  b.rect(back - 25, -s.H, back, 0);
  b.rect(25, -s.H, back - 25, -s.H + 25);
  b.rect(25, -115, back - 25, -90);
  b.line([{ x: 462, y: -128 }, { x: 462, y: -912 }], false, FINE);
  // the cabinet, behind the skid
  b.rect(back - s.cabD, -s.H, back, -s.cabBase, HIDDEN);
  // the two cells, end on
  for (const h of s.cellH) {
    b.circle({ x: s.cellFront, y: -h }, s.cellR);
    b.circle({ x: s.cellFront, y: -h }, s.cellR - 16, FINE);
    b.line([{ x: s.cellFront - s.cellR - 20, y: -h }, { x: s.cellFront + s.cellR + 20, y: -h }], false, CENTRE);
  }
  // the outlet (the inlet is behind it): from the connection at the back, through the valve, down into the top cell
  const top = s.H - s.pipeDrop;
  const cellTop = s.cellH[1] + s.cellR;
  b.line([{ x: back, y: -(top + r) }, { x: s.cellFront - r, y: -(top + r) }, { x: s.cellFront - r, y: -cellTop }]);
  b.line([{ x: back, y: -(top - r) }, { x: s.cellFront + r, y: -(top - r) }, { x: s.cellFront + r, y: -cellTop }]);
  b.rect(back - 150, -(top + 40), back - 35, -(top - 40), FINE);
  b.line([{ x: s.cellFront - 40, y: -top }, { x: back + 25, y: -top }], false, CENTRE);
  // the acid drain, end on (it points out of the far end)
  const dx = back - s.drainY;
  b.circle({ x: dx, y: -s.drainH }, s.odDrain / 2);
  b.circle({ x: dx, y: -s.drainH }, s.odDrain / 2 - 3, FINE);
  b.textAt(300, -440, "AIS AutoChlor SRC-200-100F", 22); // in the gap between the cells, clear of the pipe
  b.textAt(back - 150, -(top + r + 30), "IN / OUT DN50", 20);
  b.textAt(dx - 60, -s.drainH - 40, "DRAIN DN20", 16);
  return [
    { p: { x: back, y: -top }, dir: { x: 1, y: 0 }, size: s.dnMain },
    { p: { x: dx, y: -s.drainH }, dir: { x: 0, y: -1 }, size: s.dnDrain, vertical: true }
  ];
}

const round3 = (v: number) => Math.round(v * 1000) / 1000;
const r1 = (n: number) => Math.round(n * 10) / 10;

const VIEW_WORDS: Record<ChlorinatorView, string> = { plan: "plan", "plan-clearance": "plan with service clearance", side: "end view" };

/** One built-in object: the view drawn, centred on 0,0, as ONE group with its ports. */
export function chlorinatorItem(view: ChlorinatorView): LibraryItem {
  const key = `chlor-ais-src-200-100f-${view}`;
  const b = new Builder(key);
  const ports = view === "side" ? side(b) : plan(b);
  if (view === "plan-clearance") clearance(b);
  const box = boundsOf(b.entities);
  const c = { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 };
  const mv = (p: Vec): Vec => ({ x: round3(p.x - c.x), y: round3(p.y - c.y) });
  const entities: Entity[] = b.entities.map((e) => {
    if (e.type === "polyline") return { ...e, points: e.points.map(mv) };
    if (e.type === "circle" || e.type === "arc") return { ...e, center: mv(e.center), r: round3(e.r) };
    if (e.type === "text") return { ...e, pos: mv(e.pos) };
    return e;
  });
  const info: EquipmentInfo = { kind: "chlorinator", maker: "AIS Water", model: "AutoChlor SRC-200-100F", view, source: "AIS Water SRC-200-100F technical datasheet (2025), page 2", portNames: view === "side" ? ["outlet (inlet behind)", "drain"] : ["inlet", "outlet", "drain"] };
  entities.push({
    id: `${key}-group`,
    type: "group",
    layerId: "equipment",
    locked: false,
    hidden: false,
    children: entities.map((e) => e.id),
    meta: { equipment: info, ports: ports.map((pt) => ({ p: mv(pt.p), dir: { ...pt.dir }, size: pt.size, ...(pt.vertical ? { vertical: true } : {}) })) }
  });
  return {
    id: `builtin:${key}`,
    name: `AIS AutoChlor SRC-200-100F chlorinator · ${VIEW_WORDS[view]} · in/out DN50, drain DN20`,
    category: CHLORINATOR_CATEGORY,
    entities,
    size: { w: r1(box.maxX - box.minX), h: r1(box.maxY - box.minY) },
    units: "mm",
    updatedAt: "2026-10-27T00:00:00.000Z"
  };
}

let cache: LibraryItem[] | null = null;

/** Every built-in chlorinator object: the SRC-200-100F plan, plan with service clearance, and end view. */
export function builtInChlorinators(): LibraryItem[] {
  if (!cache) cache = (["plan", "plan-clearance", "side"] as const).map(chlorinatorItem);
  return cache;
}
